import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { Button, Modal, Checkbox, Form, Input, Popconfirm, Select } from 'antd';

import { Container } from '@/ui';
import { useQuestionnaire } from '@/features/constructor/hooks';
import { QuestionnaireSectionList } from '@/features/constructor/components/QuestionnaireSectionList';
import { QuestionnaireSection } from '@/features/constructor/components/QuestionnaireSection';
import { Box, NestedBox, ActionBox, Space } from '@/features/constructorCommon/styled';
import { DependedDocument } from '@/features/constructor/components/DependedDocument';
import { CheckTriggers } from '@/features/constructor/components/CheckTriggers';
import { ScoringRiskCategories } from '@/features/constructor/components/ScoringRiskCategories';
import { areArraysEqual } from '@/utils/helpers';
import { ConstructorForm } from '@/components/admin/molecules';

const { TextArea } = Input;

const mailingOptions = [
  {
    label: 'None',
    value: null
  },
  {
    label: 'Questionnaire all',
    value: 'QUESTIONNAIRE_ALL'
  },
  {
    label: 'Consultation',
    value: 'CONSULTATION'
  }
];

export const Questionnaire = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    data,
    isError,
    isLoading,
    handleCreate,
    handleDelete,
    isDeleting,
    handleUpdate,
    isUpdating
  } = useQuestionnaire(id);
  const [questionnaire, setQuestionnaire] = useState({});
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [isDepends, setIsDepends] = useState(false);
  const [isScoring, setIsScoring] = useState(false);
  const [isRelatedQuestionnaire, setIsRelatedQuestionnaire] = useState(false);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [isModalRiskCategoriesOpened, setIsModalRiskCategoriesOpened] = useState(false);
  const [isModalRelatedQuestionnaireOpened, setIsModalRelatedQuestionnaireOpened] = useState(false);

  useEffect(() => {
    if (!data?.id) return;

    setQuestionnaire(data);

    if (data?.kycTypeDocuments?.length) setIsDepends(true);
    if (data?.relatedQuestionnaires?.length) setIsRelatedQuestionnaire(true);
    if (data?.isScoring) setIsScoring(data?.isScoring);
  }, [data]);

  const onChange = (e) => {
    const { name, value } = e.target;
    const valueModified = name === 'expirationMonths' ? Number(value) : value;
    setQuestionnaire({ ...questionnaire, [name]: valueModified });
  };

  const onDelete = () => {
    handleDelete(questionnaire);
    navigate(`/constructor/`);
  };

  const onChangeRole = (e) => {
    const { name, checked } = e.target;

    const initialRoles = [...questionnaire.roles];
    const newRoles = checked
      ? [...initialRoles, name]
      : initialRoles.filter((item) => item !== name);

    setQuestionnaire({ ...questionnaire, roles: newRoles });
  };

  const onChangeIsCheckbox = (e) => {
    const { name, checked } = e.target;
    setQuestionnaire({ ...questionnaire, [name]: checked });
  };

  const handleAction = () => {
    if (questionnaire.id) {
      handleUpdate(questionnaire);
    } else {
      handleCreate(questionnaire).then((res) => {
        if (res?.data?.id) {
          navigate(`/constructor/questionnaires/${res.data.id}`);
        }
      });
    }
  };

  if (isError && !data) {
    return (
      <Container $paddingY={24}>
        <div>{t('Error')}</div>
      </Container>
    );
  }

  const isDisabled =
    !questionnaire.type ||
    (questionnaire.type === data?.type &&
      questionnaire.name === data?.name &&
      questionnaire.title === data?.title &&
      questionnaire.descriptions === data?.descriptions &&
      questionnaire.allowAdminAdd === data?.allowAdminAdd &&
      questionnaire.isRequired === data?.isRequired &&
      questionnaire.onlyAdmin === data?.onlyAdmin &&
      areArraysEqual(questionnaire.roles, data?.roles) &&
      questionnaire.percentAllowedCorrectAnswers === data?.percentAllowedCorrectAnswers &&
      questionnaire.firstIntervalDay === data?.firstIntervalDay &&
      questionnaire.nextIntervalDay === data?.nextIntervalDay &&
      questionnaire.autoCheck === data?.autoCheck &&
      questionnaire.prohibitAddingWithoutDependentQuestionnaire ===
        data?.prohibitAddingWithoutDependentQuestionnaire &&
      questionnaire.mailing === data?.mailing &&
      questionnaire.isScoring === data?.isScoring &&
      questionnaire.isSelectTotalPoints === data?.isSelectTotalPoints &&
      questionnaire.isSelectCategoryScoring === data?.isSelectCategoryScoring &&
      questionnaire.isSelectScoringTooltip === data?.isSelectScoringTooltip &&
      questionnaire.allowAgentAdd === data?.allowAgentAdd &&
      questionnaire.allowRecommenderAdd === data?.allowRecommenderAdd &&
      questionnaire.expirationMonths === data?.expirationMonths);

  if (isLoading) return <div>{t('Loading')}...</div>;

  return (
    <Container $paddingY={20}>
      <Box
        $isNew={true}
        styles={{
          body: {
            padding: '16px'
          }
        }}
      >
        <ConstructorForm>
          <Form.Item label={t('Title')}>
            <Input name='title' value={questionnaire.title} onChange={onChange} />
          </Form.Item>

          <Form.Item label={t('Name')}>
            <Input name='name' value={questionnaire.name} onChange={onChange} />
          </Form.Item>

          <Form.Item label={t('Type')}>
            <Input name='type' value={questionnaire.type} onChange={onChange} />
          </Form.Item>

          <Form.Item label={t('Description')}>
            <TextArea
              name='descriptions'
              value={questionnaire.descriptions}
              onChange={onChange}
              rows={4}
            />
          </Form.Item>

          {questionnaire.id && (
            <>
              <Form.Item label={t('Roles that are allowed to add the questionnaire')}>
                <div style={{ display: 'inline-block' }}>
                  <Checkbox
                    name='PERSONAL'
                    checked={questionnaire?.roles?.includes('PERSONAL')}
                    onChange={onChangeRole}
                    style={{ marginRight: '15px' }}
                  >
                    {t('PERSONAL')}
                  </Checkbox>
                  <Checkbox
                    name='COMPANY'
                    checked={questionnaire?.roles?.includes('COMPANY')}
                    onChange={onChangeRole}
                    style={{ marginRight: '15px' }}
                  >
                    {t('COMPANY')}
                  </Checkbox>
                  <Checkbox
                    name='AGENT'
                    checked={questionnaire?.roles?.includes('AGENT')}
                    onChange={onChangeRole}
                    style={{ marginRight: '15px' }}
                  >
                    {t('AGENT')}
                  </Checkbox>
                  <Checkbox
                    name='RECOMMENDER'
                    checked={questionnaire?.roles?.includes('RECOMMENDER')}
                    onChange={onChangeRole}
                    style={{ marginRight: '15px' }}
                  >
                    {t('ASSISTANT')}
                  </Checkbox>
                </div>
              </Form.Item>

              <Form.Item
                label={t('Roles that are allowed to add the questionnaire for their referrals')}
              >
                <div style={{ display: 'inline-block' }}>
                  <Checkbox
                    name='allowAgentAdd'
                    checked={questionnaire?.allowAgentAdd}
                    onChange={onChangeIsCheckbox}
                    style={{ marginRight: '15px' }}
                  >
                    {t('AGENT')}
                  </Checkbox>
                  <Checkbox
                    name='allowRecommenderAdd'
                    checked={questionnaire?.allowRecommenderAdd}
                    onChange={onChangeIsCheckbox}
                    style={{ marginRight: '15px' }}
                  >
                    {t('ASSISTANT')}
                  </Checkbox>
                </div>
              </Form.Item>

              <Form.Item>
                <Checkbox
                  name='allowAdminAdd'
                  checked={questionnaire?.allowAdminAdd}
                  onChange={onChangeIsCheckbox}
                  style={{ marginRight: '15px' }}
                >
                  {t('Admin is allowed to add the questionnaire for investors')}
                </Checkbox>
              </Form.Item>

              <Form.Item>
                <Checkbox
                  name='onlyAdmin'
                  checked={questionnaire?.onlyAdmin}
                  onChange={onChangeIsCheckbox}
                >
                  {t('Only Admin')}
                </Checkbox>
              </Form.Item>

              <Form.Item>
                <Checkbox
                  name='isRequired'
                  checked={questionnaire?.isRequired}
                  onChange={onChangeIsCheckbox}
                >
                  {t('Is required (appears in profile after registration)')}
                </Checkbox>
              </Form.Item>

              <Form.Item>
                <Checkbox
                  name='autoCheck'
                  checked={questionnaire?.autoCheck}
                  onChange={onChangeIsCheckbox}
                >
                  {t('Automatic check by the system')}
                </Checkbox>
              </Form.Item>
            </>
          )}

          {questionnaire.id && (
            <>
              <Form.Item>
                <Checkbox
                  name='isScoring'
                  value={isScoring}
                  checked={isScoring}
                  onChange={(e) => {
                    onChangeIsCheckbox(e);
                    setIsScoring(e.target?.checked);
                  }}
                >
                  {t('Is scoring')}
                </Checkbox>
              </Form.Item>

              {isScoring && (
                <>
                  <Form.Item>
                    <Checkbox
                      name='isSelectTotalPoints'
                      checked={questionnaire?.isSelectTotalPoints}
                      onChange={onChangeIsCheckbox}
                    >
                      {t('Show total points')}
                    </Checkbox>
                  </Form.Item>

                  <Form.Item>
                    <Checkbox
                      name='isSelectScoringTooltip'
                      checked={questionnaire?.isSelectScoringTooltip}
                      onChange={onChangeIsCheckbox}
                    >
                      {t('Show points for answer in tooltip')}
                    </Checkbox>
                  </Form.Item>

                  <Form.Item>
                    <Checkbox
                      name='isSelectCategoryScoring'
                      checked={questionnaire?.isSelectCategoryScoring}
                      onChange={onChangeIsCheckbox}
                    >
                      {t('Show categories scoring')}
                    </Checkbox>
                  </Form.Item>

                  <Button onClick={() => setIsModalRiskCategoriesOpened(true)} type='primary'>
                    {t('Add risk categories')}
                  </Button>

                  <Modal
                    title={t('Add risk categories')}
                    width={700}
                    style={{
                      top: 20
                    }}
                    okText={'Add'}
                    footer={null}
                    open={isModalRiskCategoriesOpened}
                    onCancel={() => setIsModalRiskCategoriesOpened(false)}
                  >
                    <ScoringRiskCategories
                      questionSectionTypeId={id}
                      closeModal={() => setIsModalRiskCategoriesOpened(false)}
                      isMultiple={true}
                    />
                  </Modal>
                </>
              )}
            </>
          )}

          {questionnaire.autoCheck && (
            <>
              <Form.Item label={`${t('Percent Allowed Correct Answers')} (0-100%)`}>
                <Input
                  name='percentAllowedCorrectAnswers'
                  type='number'
                  min={0}
                  max={100}
                  step={1}
                  value={questionnaire.percentAllowedCorrectAnswers}
                  onChange={onChange}
                  rows={4}
                />
              </Form.Item>

              <Form.Item
                label={t('Number Of Days After First Failure Until Next Attempt (Default 3)')}
              >
                <Input
                  name='firstIntervalDay'
                  type='number'
                  min={0}
                  max={1000}
                  step={1}
                  value={questionnaire.firstIntervalDay}
                  onChange={onChange}
                />
              </Form.Item>

              <Form.Item
                label={t(
                  'Number Of Days After Second And Next Failures Until Next Attempt (Default 7)'
                )}
              >
                <Input
                  name='nextIntervalDay'
                  type='number'
                  min={0}
                  max={1000}
                  step={1}
                  value={questionnaire.nextIntervalDay}
                  onChange={onChange}
                />
              </Form.Item>
            </>
          )}

          {questionnaire.id && (
            <>
              <Form.Item>
                <Checkbox
                  name='isDepends'
                  value={isDepends}
                  checked={isDepends}
                  onChange={(e) => setIsDepends(e.target.checked)}
                >
                  {t('This questionnaire contains dependent document types')}
                </Checkbox>
              </Form.Item>

              {isDepends && (
                <>
                  <div>
                    {questionnaire.kycTypeDocuments?.map((documentType, index) => (
                      <div key={index}>
                        <Input name={`documentType-${index}`} value={documentType.name} />
                        <Space />
                      </div>
                    ))}
                  </div>

                  <Button onClick={() => setIsModalOpened(true)} type='primary'>
                    {t('Select dependent document types')}
                  </Button>
                  <Modal
                    title={t('Select dependent document types')}
                    width={700}
                    style={{
                      top: 20
                    }}
                    okText={t('Add')}
                    footer={null}
                    open={isModalOpened}
                    onCancel={() => setIsModalOpened(false)}
                  >
                    <DependedDocument
                      kycTypeDocuments={questionnaire.kycTypeDocuments}
                      questionSectionTypeId={questionnaire.id}
                    />
                  </Modal>
                </>
              )}

              <Form.Item>
                <Checkbox
                  name='isRelatedQuestionnaire'
                  value={isRelatedQuestionnaire}
                  checked={isRelatedQuestionnaire}
                  onChange={(e) => setIsRelatedQuestionnaire(e.target.checked)}
                >
                  {t('This questionnaire depends on another questionnaire')}
                </Checkbox>
              </Form.Item>

              {isRelatedQuestionnaire && (
                <>
                  <Button onClick={() => setIsModalRelatedQuestionnaireOpened(true)} type='primary'>
                    {t('Check Triggers')}
                  </Button>
                  <Modal
                    title={t('Triggers')}
                    width={700}
                    style={{
                      top: 20
                    }}
                    okText={t('Add')}
                    footer={null}
                    open={isModalRelatedQuestionnaireOpened}
                    onCancel={() => setIsModalRelatedQuestionnaireOpened(false)}
                  >
                    <CheckTriggers questionnaire={questionnaire} />
                  </Modal>
                </>
              )}
              <Form.Item label={t('Mailing')}>
                <Select
                  name='mailing'
                  onChange={(value) => setQuestionnaire({ ...questionnaire, mailing: value })}
                  value={questionnaire.mailing}
                  placeholder={t('Select mailing')}
                  style={{ width: '100%' }}
                >
                  {mailingOptions.map((item, index) => (
                    <Select.Option key={index} value={item.value}>
                      {t(item.label)}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label={t('Expiration Months (default - 12)')}>
                <Input
                  name='expirationMonths'
                  type='number'
                  min={0}
                  step={1}
                  value={questionnaire.expirationMonths}
                  onChange={onChange}
                />
              </Form.Item>
            </>
          )}

          <ActionBox>
            <Button
              onClick={handleAction}
              disabled={isDisabled}
              type='primary'
              loading={isUpdating}
            >
              {questionnaire.id ? t('Update Questionnaire') : t('Create Questionnaire')}
            </Button>

            {questionnaire.id && (
              <Popconfirm
                placement='topRight'
                title={t('Are you sure to delete this questionnaire?')}
                onConfirm={onDelete}
                okText={t('Yes')}
                cancelText={t('No')}
              >
                <Button danger loading={isDeleting}>
                  {t('Delete Questionnaire')}
                </Button>
              </Popconfirm>
            )}
          </ActionBox>
        </ConstructorForm>
      </Box>

      {questionnaire.id && (
        <Container $paddingY={20}>
          <NestedBox>
            <QuestionnaireSectionList
              autoCheck={questionnaire.autoCheck}
              isScoring={questionnaire.isScoring}
              questionnaireId={id}
            />

            {showCreateNew ? (
              <QuestionnaireSection onCreated={() => setShowCreateNew(false)} />
            ) : (
              <Button onClick={() => setShowCreateNew(true)}>{t('Add new Section')}</Button>
            )}
          </NestedBox>
        </Container>
      )}
    </Container>
  );
};
